/**
 * Offline Manager for handling service worker registration and offline detection
 */

interface OfflineState {
  isOnline: boolean;
  isServiceWorkerRegistered: boolean;
  isServiceWorkerActive: boolean;
}

export class OfflineManager {
  private static instance: OfflineManager;
  private offlineState: OfflineState = {
    isOnline: navigator.onLine,
    isServiceWorkerRegistered: false,
    isServiceWorkerActive: false
  };

  private listeners: Set<(state: OfflineState) => void> = new Set();

  private constructor() {
    this.setupEventListeners();
  }

  static getInstance(): OfflineManager {
    if (!OfflineManager.instance) {
      OfflineManager.instance = new OfflineManager();
    }
    return OfflineManager.instance;
  }

  private setupEventListeners(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.updateOnlineState(true);
    });

    window.addEventListener('offline', () => {
      this.updateOnlineState(false);
    });
  }

  private updateOnlineState(isOnline: boolean): void {
    this.offlineState.isOnline = isOnline;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.offlineState));
  }

  /**
   * Register the service worker
   */
  async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('./sw.js', {
        scope: './'
      });

      console.log('Service Worker registered:', registration);

      this.offlineState.isServiceWorkerRegistered = true;

      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              console.log('New service worker version available');
              // Optionally show update prompt to user
            }
          });
        }
      });

      // Check if service worker is active
      if (registration.active) {
        this.offlineState.isServiceWorkerActive = true;
      }

      // Listen for controller change (when SW takes control)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Service Worker controller changed');
        this.offlineState.isServiceWorkerActive = true;
        this.notifyListeners();
      });

      this.notifyListeners();
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  /**
   * Get current offline state
   */
  getOfflineState(): OfflineState {
    return { ...this.offlineState };
  }

  /**
   * Subscribe to offline state changes
   */
  subscribe(listener: (state: OfflineState) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.offlineState);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Check if the app can work offline
   */
  canWorkOffline(): boolean {
    return this.offlineState.isServiceWorkerActive;
  }

  /**
   * Force refresh the page (for SW updates)
   */
  forceRefresh(): void {
    window.location.reload();
  }
}

export default OfflineManager;